import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Rocket, Lock, Loader2, Clapperboard, Facebook, Twitter, Linkedin, MessageCircle, Link2 } from 'lucide-react';
import { API, errText } from '../../api';
import { songLabel } from '../../lib/song';
import LaunchVideos from './LaunchVideos';
import LaunchCaptions from './LaunchCaptions';
import AiPhotoVideo from './AiPhotoVideo';

const SHARE = (url, text) => [
  { id: 'facebook', label: 'Facebook', Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
  { id: 'x', label: 'X', Icon: Twitter, href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}` },
  { id: 'linkedin', label: 'LinkedIn', Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}` },
  { id: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle, href: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}` },
];

export default function LaunchPad({ req, plans }) {
  const owned = (req.versions || []).filter((v) => (req.purchased_versions || []).includes(v.id));
  const [data, setData] = useState(null);
  const [version, setVersion] = useState(owned[0]?.id);
  const [busy, setBusy] = useState(false);
  const [capBusy, setCapBusy] = useState(false);
  const [buying, setBuying] = useState(null);
  const timer = useRef(null);

  const load = useCallback(() => API.launch(req.code).then(setData).catch(() => {}), [req.code]);
  useEffect(() => { load(); }, [load]);
  const rendering = data?.videos?.some((v) => v.status === 'rendering');
  useEffect(() => {
    if (!rendering) return undefined;
    timer.current = setInterval(load, 5000);
    return () => clearInterval(timer.current);
  }, [rendering, load]);

  const buyLaunchPad = async (tier) => {
    setBuying(tier);
    try { const { checkout_url } = await API.checkout({ kind: 'launchpad', code: req.code, tier }); window.location.assign(checkout_url); }
    catch (e) { toast.error(errText(e, 'Could not start checkout.')); setBuying(null); }
  };
  const makeVideo = async () => {
    setBusy(true);
    try { await API.launchVideo(req.code, version); toast.success('Producing your videos.', { description: 'About a minute per format. You can leave this page.' }); await load(); }
    catch (e) { toast.error(errText(e, 'Could not start the video.')); }
    finally { setBusy(false); }
  };
  const del = async (vid) => {
    try { await API.deleteLaunchVideo(req.code, vid); await load(); } catch (e) { toast.error('Could not delete.'); }
  };
  const genCaptions = async () => {
    setCapBusy(true);
    try { const c = await API.launchCaptions(req.code, version); setData((d) => ({ ...d, captions: c })); toast.success('Captions ready.'); }
    catch (e) { toast.error(errText(e, 'Could not write captions.')); }
    finally { setCapBusy(false); }
  };
  const saveCaptions = async (c) => {
    try { await API.saveLaunchCaptions(req.code, c); setData((d) => ({ ...d, captions: c })); toast.success('Saved.'); }
    catch (e) { toast.error('Could not save.'); }
  };

  if (!owned.length || !data) return null;
  const shareUrl = `${window.location.origin}/song/${req.code}`;
  const shareText = `Our business has a song! Listen to \u201c${songLabel(owned.find((v) => v.id === version) || owned[0])}\u201d`;

  if (!data.unlocked) {
    const lp = plans?.launchpad;
    return (
      <div className="mt-7 rounded-3xl border border-gold/25 bg-gold/[0.03] p-6" data-testid="launch-pad-locked">
        <p className="flex items-center gap-2 font-display text-2xl text-cream"><Rocket size={20} className="text-gold" /> Social Media Launch Pad</p>
        <p className="text-sm text-cream/65 mt-2 leading-relaxed">Turn your song into ready-to-post videos for Instagram, TikTok, Facebook, YouTube and X, plus captions and hashtags in your voice and one-tap share buttons. Ready within minutes of purchase.</p>
        {lp && (
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            {Object.entries(lp).map(([id, p]) => (
              <div key={id} className={`rounded-2xl border p-4 flex flex-col ${id === 'pro' ? 'border-gold/50 bg-gold/[0.04]' : 'hairline'}`} data-testid={`launchpad-option-${id}`}>
                <div className="flex items-baseline justify-between gap-2"><p className="font-display text-xl text-cream">{p.name}</p><p className="font-display text-2xl text-cream">${p.price}</p></div>
                <ul className="mt-2 text-xs text-cream/55 space-y-1 flex-1">{p.features.slice(0, 4).map((f) => <li key={f}>&bull; {f}</li>)}</ul>
                <button onClick={() => buyLaunchPad(id)} disabled={buying} data-testid={`launchpad-buy-${id}`} className={`mt-4 rounded-full py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50 ${id === 'pro' ? 'btn-gold' : 'btn-ghost'}`}>{buying === id ? <Loader2 size={13} className="animate-spin" /> : <Lock size={13} />} Buy {p.name} &middot; ${p.price}</button>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-cream/40 mt-3">Secure checkout by Stripe. Pro&rsquo;s AI photo video is delivered by Perry within 48 hours; everything else unlocks instantly.</p>
      </div>
    );
  }

  return (
    <div className="mt-7 rounded-3xl border border-gold/25 bg-gold/[0.03] p-6 space-y-5" data-testid="launch-pad">
      <div>
        <p className="flex items-center gap-2 font-display text-2xl text-cream"><Rocket size={20} className="text-gold" /> Social Launch Pad</p>
        <p className="text-sm text-cream/60 mt-1">Pick a song, make your videos, grab your captions, post everywhere.</p>
      </div>
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <label className="eyebrow text-[9px] block mb-2" htmlFor="launch-version">Song</label>
          <select id="launch-version" value={version} onChange={(e) => setVersion(e.target.value)} data-testid="launch-version-select" className="w-full rounded-xl border hairline bg-panel2/50 px-4 py-3 text-sm text-cream outline-none focus:border-gold/50">
            {owned.map((v) => <option key={v.id} value={v.id}>{songLabel(v)}</option>)}
          </select>
        </div>
        <button onClick={makeVideo} disabled={busy || rendering} data-testid="launch-make-video-btn" className="btn-gold rounded-full px-6 py-3 text-sm flex items-center gap-2 disabled:opacity-50">{busy || rendering ? <Loader2 size={14} className="animate-spin" /> : <Clapperboard size={14} />} {rendering ? 'Producing…' : 'Make my videos'}</button>
      </div>
      <AiPhotoVideo key={data.level + (data.photos || []).length} req={req} data={data} version={version} rendering={rendering} onChanged={load} />
      <LaunchVideos videos={data.videos || []} onDelete={del} />
      <LaunchCaptions captions={data.captions} onGenerate={genCaptions} onSave={saveCaptions} busy={capBusy} />
      <div className="rounded-2xl border hairline p-5" data-testid="launch-share">
        <p className="flex items-center gap-2 text-sm text-cream"><Link2 size={15} className="text-gold" /> Share your song page</p>
        <p className="text-xs text-cream/50 mt-1">{req.share_enabled ? 'These open a post with your song page link already filled in.' : 'Turn on \u201cShare your song\u201d above first so the link works for visitors.'}</p>
        <div className="mt-3 flex gap-2 flex-wrap">
          {SHARE(shareUrl, shareText).map(({ id, label, Icon, href }) => (
            <a key={id} href={href} target="_blank" rel="noreferrer" data-testid={`share-${id}`} className="btn-ghost rounded-full px-4 py-2 text-xs flex items-center gap-2"><Icon size={13} /> {label}</a>
          ))}
        </div>
        <p className="text-[11px] text-cream/40 mt-3">Instagram, TikTok and YouTube only accept uploads from their own apps &mdash; download the matching video above and post it there with your copied caption.</p>
      </div>
    </div>
  );
}
